import { useCallback } from 'react'
import { fireTransition } from '../application/fire-transition'
import { resolveItemPhaseTeamId } from '../domain/authorization'
import type { CompanyWorkflow, WorkItem } from '../domain/workflow'
import type { KanbanMoveEvent } from '@/components/kanban'
import { workItems$ } from '../store'
import { workItems as workItemsPort, notify } from '../wiring'
import { getWorkItemActor } from './use-work-item-actor'

const findTransitionToStep = (workflow: CompanyWorkflow, workItem: WorkItem, targetStepId: string): string | null => {
	for (const phase of workflow.phases) {
		for (const step of phase.teamWorkflow.steps) {
			if (step.id !== workItem.currentStepId) continue
			for (const transition of step.transitions) {
				if (transition.target.kind === 'step' && transition.target.stepId === targetStepId) {
					return transition.id
				}
			}
		}
	}
	return null
}

// True when targetStepId is the immediately previous step (order - 1) in the
// same phase as the item's current step. Such moves are always allowed and
// bypass the transition graph.
const isPreviousStepInPhase = (workflow: CompanyWorkflow, workItem: WorkItem, targetStepId: string): boolean => {
	for (const phase of workflow.phases) {
		const steps = phase.teamWorkflow.steps
		const current = steps.find(s => s.id === workItem.currentStepId)
		const target = steps.find(s => s.id === targetStepId)
		if (!current || !target) continue
		return target.order === current.order - 1
	}
	return false
}

const useFireTransition = (workflow: CompanyWorkflow) => {
	const fire = useCallback(async (item: WorkItem, transitionId: string) => {
		const ctx = getWorkItemActor()
		const phaseTeamId = resolveItemPhaseTeamId(workflow.phases, item)
		const updated = await fireTransition(workItemsPort, item, transitionId, ctx, notify, phaseTeamId)
		if (updated) {
			workItems$.set(workItems$.get().map(w => w.id === updated.id ? updated : w))
		}
		return updated
	}, [workflow])

	const moveBack = useCallback(async (item: WorkItem, stepId: string) => {
		const ctx = getWorkItemActor()
		try {
			const updated = await workItemsPort.moveToStep(item.id, stepId, ctx.actorId, ctx.actorName)
			workItems$.set(workItems$.get().map(w => w.id === updated.id ? updated : w))
			return updated
		} catch {
			notify.error('workItem.moveFailed')
			return null
		}
	}, [])

	const onMove = useCallback(async (event: KanbanMoveEvent) => {
		const { activeContainer: fromStepId, overContainer: toStepId } = event
		if (fromStepId === toStepId) return

		// Derive the dragged item from the committed server state rather than reading
		// a derived DnD-layer store. The activeIndex is the item's position within
		// its source step column at drag-start, which matches the server-ordered array.
		const item = workItems$.get().filter(w => w.currentStepId === fromStepId)[event.activeIndex]
		if (!item) return

		const transitionId = findTransitionToStep(workflow, item, toStepId)
		if (transitionId) {
			await fire(item, transitionId)
			return
		}

		// No forward transition: allow stepping back one column within the phase.
		if (isPreviousStepInPhase(workflow, item, toStepId)) {
			await moveBack(item, toStepId)
			return
		}

		notify.error('workItem.noTransitionAvailable')
	}, [workflow, fire, moveBack])

	return { onMove, fire, moveBack }
}

export { useFireTransition }
